import Layout from "@/components/Layout";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { articlesContent, articlesList } from "@/data/blogArticles";
import { ArticleJsonLd } from "@/components/JsonLd";
import SEOHead from "@/components/SEOHead";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();

  const staticArticle = slug ? articlesContent[slug] : null;

  const { data: dbArticle, isLoading } = useQuery({
    queryKey: ["blog-article", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_articles")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .maybeSingle();
      return data;
    },
    enabled: !staticArticle && !!slug,
  });

  // Fetch other articles for internal linking
  const { data: relatedArticles } = useQuery({
    queryKey: ["related-articles", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_articles")
        .select("slug, title, date")
        .eq("published", true)
        .neq("slug", slug!)
        .order("date", { ascending: false })
        .limit(3);
      return data || [];
    },
    enabled: !!slug,
  });

  const article = staticArticle || (dbArticle ? {
    title: dbArticle.title,
    description: dbArticle.description,
    date: dbArticle.date,
    content: null,
  } : null);

  if (isLoading) {
    return (
      <Layout>
        <section className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto" />
        </section>
      </Layout>
    );
  }

  if (!article || !slug) {
    return (
      <Layout>
        <SEOHead
          title="Artykuł nie znaleziony | naklejki-3d.com.pl"
          description="Nie znaleziono artykułu. Wróć do bloga naklejki-3d.com.pl."
          noindex
        />
        <section className="py-20 text-center">
          <p className="text-muted-foreground text-lg mb-4">Artykuł nie został znaleziony.</p>
          <Link to="/blog" className="text-accent font-semibold hover:underline">← Wróć do bloga</Link>
        </section>
      </Layout>
    );
  }

  // Get other static articles for internal linking (exclude current)
  const otherStaticArticles = articlesList
    .filter((a) => a.slug !== slug)
    .slice(0, 2);

  const allRelated = [
    ...otherStaticArticles.map((a) => ({ slug: a.slug, title: a.titleKey })),
    ...(relatedArticles || []).map((a) => ({ slug: a.slug, title: a.title })),
  ].slice(0, 4);

  return (
    <Layout breadcrumbTitle={article.title}>
      <SEOHead
        title={`${article.title} | naklejki-3d.com.pl`}
        description={article.description}
        ogType="article"
        articleDate={article.date}
      />
      <ArticleJsonLd title={article.title} description={article.description} date={article.date} slug={slug} />
      <section className="gradient-navy py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-5xl font-bold text-primary-foreground mb-4">{article.title}</h1>
          <p className="text-primary-foreground/60 text-sm">
            {new Date(article.date).toLocaleDateString("pl-PL", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <Link to="/blog" className="text-accent font-semibold flex items-center gap-2 mb-10 hover:underline text-sm">
            <ArrowLeft className="w-4 h-4" /> Wróć do bloga
          </Link>

          {staticArticle ? (
            staticArticle.content
          ) : dbArticle ? (
            <div
              className="prose prose-lg max-w-none [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:text-muted-foreground [&_ul]:space-y-2 [&_ul]:mb-4 [&_li]:ml-4 [&_strong]:text-foreground"
              dangerouslySetInnerHTML={{ __html: dbArticle.content }}
            />
          ) : null}

          {/* Internal linking section */}
          {allRelated.length > 0 && (
            <div className="mt-16 pt-10 border-t border-border">
              <h2 className="text-xl font-bold text-foreground mb-6">Przeczytaj również</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {allRelated.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="block p-5 bg-card border border-border rounded-lg hover:border-accent/40 hover:shadow-md transition-all"
                  >
                    <h3 className="text-foreground font-semibold mb-2 text-sm leading-snug">{r.title}</h3>
                    <span className="text-accent text-xs font-medium flex items-center gap-1">
                      Czytaj więcej <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default BlogArticle;
